import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useSnackbar } from "notistack";
import {
  Button,
  CircularProgress,
  makeStyles,
  Dialog,
  DialogActions,
  DialogContent,
  Slide,
} from "@material-ui/core";
import useToggle from "../hooks/useToggle";
import { addClassToDefaultIfTrue } from "../util/addClassToDefaultIfTrue";

import DevicePreview from "./device-preview/DevicePreview";

import { useParams } from "react-router";
import QuizEditorContext from "../contexts/QuizEditorContext";
import useAuthenticatedRestCall from "../hooks/useAuthenticatedRestCall";
import ContentEditorToolbar from "./courses/ContentEditorToolbar";
import ContentEditor from "./courses/ContentEditor";
import LanguageContext from "../contexts/LanguageContext";
import { DEFAULT_EDITOR_CONTENT } from "../constants/defaultEditorContent";
import { useDefaultLanguage } from "../hooks/useDefaultLanguage";
import DevicePreviewContext from "../contexts/DevicePreviewContext";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const useStyles = makeStyles({
  wrapper: {
    height: "100%",
    display: "flex",
    overflowY: "hidden",
    flexDirection: "column",
    "&.hidden": {
      display: "none",
    },
  },
  closePreviewButton: {
    fontSize: "14px",
    background: "rgba(0,0,0,0.3)",
    color: "white",
  },
  loadingSpinner: {
    marginTop: "4rem",
    width: "6rem !important",
    height: "6rem !important",
  },
});

function AddEditCourseContent({
  initialRichContent,
  onTitleChange,
  isLoading,
  saveSuccessMessage,
  saveRequest,
  isQuiz,
  title,
  showQuizModeToggle,
  onQuizModeChanged,
  published,
  onPublishedChanged,
  translatedContent,
  setTranslatedContent,
  translatedTitles,
  setTranslatedTitles,
  hideSaveButton,
  onTitleSave,
  onRichContentEdited,
  languageBeingEditedId,
  setLanguageBeingEditedId,
  onEditQuestion,
  onAddQuestion,
  onDeleteQuestion, // FIX: Receive prop
  children,
  isDirty,
  onDiscardChanges
}) {
  const classes = useStyles();
  const [titleError, setTitleError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewOpen, togglePreviewOpen] = useToggle();
  const { enqueueSnackbar } = useSnackbar();
  let { courseId, levelId } = useParams();
  const makeAuthenticatedRequest = useAuthenticatedRestCall();
  const { setDisplayAllErrors, emptyQuestions, emptyAnswers } =
    useContext(QuizEditorContext);
  const { languageInfo } = useContext(LanguageContext);
  const { defaultLanguage, defaultLanguageLoaded } = useDefaultLanguage();

  const [editorContent, setEditorContent] = useState(DEFAULT_EDITOR_CONTENT);
  const [previewContent, setPreviewContent] = useState(DEFAULT_EDITOR_CONTENT);

  const handleEditorChange = (newContent) => {
    setEditorContent(newContent);
    if (onRichContentEdited && !isQuiz) {
      onRichContentEdited(newContent);
    }
  };

  useEffect(() => {
    if (initialRichContent) {
      setEditorContent(initialRichContent);
    }
  }, [initialRichContent]);

  const handleTitleChange = (value) => {
    if (value.trim().length === 0) {
      setTitleError("Please provide a title");
    } else {
      setTitleError(null);
    }

    onTitleChange(value);
  };

  const displayLoadingSpinner = () =>
    isLoading ? <CircularProgress className={classes.loadingSpinner} /> : null;

  const updateRichContent = (newContentId) =>
    makeAuthenticatedRequest(
      `${process.env.REACT_APP_COURSE_SERVICE_URL}/levels/${levelId}/courses/${courseId}/content/${newContentId}/rich-content`,
      true,
      {
        data: {
          content: editorContent,
        },
        method: "PUT",
      }
    );

  const redirectOrShowError = (response) => {
    if (response && response.status === 200) {
      enqueueSnackbar(`${saveSuccessMessage}`, {
        variant: "success",
      });
    }

    setSaving(false);
  };

  const addRichContentAndRedirectOrShowError = (newContentId) => {
    updateRichContent(newContentId).then(redirectOrShowError);
  };

  const saveContent = () => {
    setSaving(true);

    return saveRequest().then((response) => {
      if (!isQuiz && response) {
        const newContentId = response.data.id;
        addRichContentAndRedirectOrShowError(newContentId);
      } else {
        redirectOrShowError(response);
      }
      return response;
    });
  };

  const saveIfNoContentErrors = () => {
    setDisplayAllErrors(true);

    if (!isQuiz || (emptyQuestions.length === 0 && emptyAnswers.length === 0)) {
      saveContent();
    } else {
      enqueueSnackbar(
        "Please fix the missing fields in your quiz before saving",
        {
          variant: "error",
        }
      );
    }
  };

  const handleSaveClicked = () => {
    if (titleError) {
      enqueueSnackbar("Please give your content a title", {
        variant: "error",
      });
    } else {
      saveIfNoContentErrors();
    }
  };

  const updateTranslatedContent = useCallback(
    (languageId, translatedContentItem) => {
      const updatedContent = { ...translatedContent };
      updatedContent[languageId] = translatedContentItem;

      setTranslatedContent(updatedContent);

      if (onRichContentEdited) {
        onRichContentEdited(updatedContent);
      }
    },
    [translatedContent, setTranslatedContent, onRichContentEdited]
  );

  const setTranslatedContentForLanguageBeingEdited = useCallback(
    (translatedContentItem) => {
      updateTranslatedContent(languageBeingEditedId, translatedContentItem);
    },
    [updateTranslatedContent, languageBeingEditedId]
  );

  const setTranslatedTitleForLanguageBeingEdited = useCallback(
    (translatedTitle) => {
      const updatedTitles = { ...translatedTitles };
      updatedTitles[languageBeingEditedId] = translatedTitle;

      setTranslatedTitles(updatedTitles);

      if (onRichContentEdited) {
        onRichContentEdited(updatedTitles);
      }
    },
    [
      setTranslatedTitles,
      translatedTitles,
      languageBeingEditedId,
      onRichContentEdited,
    ]
  );

  const copyEnglishContentIfEmpty = (languageId) => {
    if (isQuiz) return;

    if (
      defaultLanguageLoaded &&
      languageId !== defaultLanguage.id &&
      JSON.stringify(translatedContent[languageId]) ===
        JSON.stringify(DEFAULT_EDITOR_CONTENT)
    ) {
      updateTranslatedContent(languageId, editorContent);
    }
  };

  const handleLanguageBeingEditedChanged = (event) => {
    const newLanguageId = event.target.value;

    if (isDirty) {
      const confirmDiscard = window.confirm(
        "You have unsaved changes. Are you sure you want to discard them?"
      );
      
      if (confirmDiscard) {
        if (onDiscardChanges) {
          onDiscardChanges();
        }
        setLanguageBeingEditedId(newLanguageId);
        copyEnglishContentIfEmpty(newLanguageId);
      }
    } else {
      setLanguageBeingEditedId(newLanguageId);
      copyEnglishContentIfEmpty(newLanguageId);
    }
  };

  useEffect(() => {
    if (defaultLanguageLoaded && languageBeingEditedId) {
      const updatedPreviewContent =
        languageBeingEditedId === defaultLanguage.id
          ? editorContent
          : translatedContent[languageBeingEditedId];

      setPreviewContent(updatedPreviewContent);
    }
  }, [
    defaultLanguageLoaded,
    defaultLanguage,
    editorContent,
    languageBeingEditedId,
    translatedContent,
  ]);

  return (
    <DevicePreviewContext.Provider value={{ previewOpen }}>
      <Fragment>
        {displayLoadingSpinner()}
        <div
          className={addClassToDefaultIfTrue(
            classes.wrapper,
            isLoading,
            "hidden"
          )}
        >
          <ContentEditorToolbar
            title={title}
            translatedTitles={translatedTitles}
            onTitleChange={handleTitleChange}
            titleError={titleError}
            saving={saving}
            onSaveClick={handleSaveClicked}
            togglePreviewOpen={togglePreviewOpen}
            quizMode={isQuiz}
            showQuizModeToggle={showQuizModeToggle}
            onQuizModeChanged={onQuizModeChanged}
            published={published}
            onPublishedChanged={onPublishedChanged}
            languageBeingEditedId={languageBeingEditedId}
            onLanguageBeingEditedChanged={handleLanguageBeingEditedChanged}
            setTranslatedTitleForLanguageBeingEdited={
              setTranslatedTitleForLanguageBeingEdited
            }
            hideSaveButton={hideSaveButton}
            onTitleSave={onTitleSave}
          />
          <ContentEditor
            saving={saving}
            loading={isLoading}
            quizMode={isQuiz}
            editorContent={editorContent}
            setEditorContent={handleEditorChange}
            untranslatedTitle={title}
            languageToEditId={languageBeingEditedId}
            translationContent={translatedContent}
            setTranslationContent={setTranslatedContent}
            setTranslatedContentForLanguageBeingEdited={
              setTranslatedContentForLanguageBeingEdited
            }
            translatedTitle={translatedTitles[languageBeingEditedId]}
            onTranslatedTitleChange={setTranslatedTitleForLanguageBeingEdited}
            onEditQuestion={onEditQuestion}
            onAddQuestion={onAddQuestion}
            onDeleteQuestion={onDeleteQuestion} // FIX: Pass down
            onSave={handleSaveClicked}
          >
            {isQuiz ? null : children}
          </ContentEditor>
        </div>
        <Dialog
          open={previewOpen}
          onClose={togglePreviewOpen}
          TransitionComponent={Transition}
          keepMounted
          PaperProps={{
            style: {
              backgroundColor: "transparent",
              boxShadow: "none",
            },
          }}
        >
          <DialogContent>
            <DevicePreview title={title} previewContent={previewContent} />
          </DialogContent>
          <DialogActions style={{ justifyContent: "center" }}>
            <Button
              variant="outlined"
              color="primary"
              size="medium"
              className={classes.closePreviewButton}
              onClick={togglePreviewOpen}
            >
              Close Preview
            </Button>
          </DialogActions>
        </Dialog>
      </Fragment>
    </DevicePreviewContext.Provider>
  );
}

export default AddEditCourseContent;